import { useEffect, useState } from "react";
import { LinearProgress, Box, Typography, Button, CircularProgress } from "@mui/material";
import dayjs from "dayjs";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
} from "@tanstack/react-table";
import request from "../../../utilities/requests";

export default function SalesVelocity({ data }) {
  const [rows, setRows] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [pairingType, setPairingType] = useState("");
  const [avgPerc, setAvgPerc] = useState({});
  const [initialLoaded, setInitialLoaded] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (data && !initialLoaded) {
      setRows(data.data);
      setAvgPerc(data.averages);
      setStartDate(dayjs(data.start).format("YYYY-MM-DD"));
      setEndDate(dayjs(data.end).format("YYYY-MM-DD"));
      setInitialLoaded(true);
    }
  }, [data]);

  const getData = async () => {
    setLoading(true)
    const url = `reports/?reportName=salesVelocity&startDate=${startDate}&endDate=${endDate}&pairingEventType=${pairingType}`;
    const result = await request(url);
    if (result.success) {
      setRows(result.data.data || []);
      setAvgPerc(result.data.averages || {});
    }
    setLoading(false);
  };


  const columns = [
    { accessorKey: "name", header: "Event Name", size: 250 },
    {
      accessorKey: "eventDate",
      header: "Date",
      meta: { align: 'center' },
      size: 80,
      cell: (info) => {
        const val = info.getValue();
        if (!val) return "";
        const d = dayjs(val);
        return d.isValid() ? d.format("M/D/YY") : "";
      },
      sortingFn: (a, b) =>
        dayjs(a.getValue()).valueOf() - dayjs(b.getValue()).valueOf(),
    },
    { accessorKey: "capacity", header: "Cap.", meta: { align: 'center' }, size: 60 },
    { accessorKey: "totalOrders", header: "Orders", meta: { align: 'center' }, size: 60 },
    {
      accessorKey: "perc_sold_by_30d_out",
      header: "30 d",
      size: 100,
      cell: (info) => (
        <PctBar value={info.getValue()} avg={avgPerc.perc_sold_by_30d_out} />
      ),
    },
    {
      accessorKey: "perc_sold_by_14d_out",
      header: "14 d",
      size: 100,
      cell: (info) => (
        <PctBar value={info.getValue()} avg={avgPerc.perc_sold_by_14d_out} />
      ),
    },
    {
      accessorKey: "perc_sold_by_7d_out",
      header: "7 d",
      size: 100,
      cell: (info) => (
        <PctBar value={info.getValue()} avg={avgPerc.perc_sold_by_7d_out} />
      ),
    },
    {
      accessorKey: "perc_sold_by_1d_out",
      header: "1 d",
      size: 100,
      cell: (info) => (
        <PctBar value={info.getValue()} avg={avgPerc.perc_sold_by_1d_out} />
      ),
    },
  ];

  const table = useReactTable({
    data: rows,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <Box sx={{ height: 600, width: "100%", padding: 2 }}>
      <Box
        sx={{
          mb: 2,
          display: "flex",
          gap: 2,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <TextField
          label="Start Date"
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          size="small"
        />
        <TextField
          label="End Date"
          type="date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          size="small"
        />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel id="pairing-type-label">Event Type</InputLabel>
          <Select
            labelId="pairing-type-label"
            label="Event Type"
            value={pairingType}
            onChange={(e) => setPairingType(e.target.value)}
          >
            <MenuItem value="">All</MenuItem>
            {Array.from(new Set(rows.map((r) => r.pairingEventType))).map(
              (type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              )
            )}
          </Select>
        </FormControl>
        <Button variant="contained" style={{ height: 35 }} onClick={getData}>
          {loading ? <CircularProgress style={{color: 'white'}} size={24}/> : "Run"}
        </Button>
      </Box>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <th key={header.id} style={{ borderBottom: "1px solid #ccc", width: header.column.columnDef.size, textAlign: header.column.columnDef.meta?.align || 'left' }}>
                  {flexRender(
                    header.column.columnDef.header ||
                      header.column.columnDef.headerName,
                    header.getContext()
                  )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => (
            <tr key={row.id}>
              {row.getVisibleCells().map((cell) => (
                <td key={cell.id} style={{ padding: "4px", width: cell.column.columnDef.size, textAlign: cell.column.columnDef.meta?.align || 'left' }}>
                  {flexRender(
                    cell.column.columnDef.cell ||
                      cell.column.columnDef.renderCell ||
                      cell.column.columnDef.headerName ||
                      cell.column.columnDef.header,
                    cell.getContext()
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </Box>
  );
}

// A little helper to show the % as a colored bar + label
function PctBar({ value, avg }) {
  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        display: "flex",
        alignItems: "center",
      }}
    >
      {/* Cumulative bar */}
      <Box sx={{ flexGrow: 1, mr: 1, position: "relative" }}>
        <LinearProgress
          variant="determinate"
          value={Math.min(value, 100)}
          sx={{
            height: 8,
            borderRadius: 1,
            backgroundColor: "#eee",
            "& .MuiLinearProgress-bar": {
              backgroundColor: value >= 100 ? "#4caf50" : "#1976d2",
            },
          }}
        />
        {/* Average marker line */}
        {avg != null && (
          <Box
            sx={{
              position: "absolute",
              left: `${Math.min(avg, 100)}%`,
              top: 0,
              bottom: 0,
              width: "2px",
              bgcolor: "red",
            }}
          />
        )}
      </Box>
      <Typography variant="body2" sx={{ minWidth: 30, fontSize: 12 }}>
        {value}%
      </Typography>
    </Box>
  );
}
